/**
 * The address a one-time invitation link points at: `/invite`, with the token in the **fragment**.
 *
 * ⚠️ **The server composes the whole path** (`/invite#<token>` — `_invitation_link` in
 * `apps/accounts/api.py`) and this constant is its client half: the route the SPA answers at. The
 * **origin** is added by whichever screen renders the link (`InvitationDialog`), because a tenant may
 * have several branded hosts and the server cannot know which one the Admin is looking at.
 *
 * ⚠️ **The token is a URL fragment rather than a path segment, and that is the fix rather than a
 * preference** (BUG-028, 2026-08-05). It was `/invite/<token>`. `/invite/…` matches `location /` in
 * the platform nginx and is proxied to the frontend container; nginx's default `combined` format logs
 * `$request`, and the SPA's own server logs the same line — so a bearer credential that **sets a
 * password**, live for seven days, was written into two access logs, their rotations and their
 * backups. A fragment is the one part of a URL a browser never sends to any server, so the same
 * secret now reaches the screen without reaching a log or a `Referer` header. A query string would be
 * no better than the path: `$request` carries that too.
 *
 * ⚠️ **What the fragment does not fix.** The token is still in browser history, still on screen in
 * the address bar and still copyable — a forwarded link is a forwarded credential either way. What
 * changed is only that it no longer travels to a server. The rest is bounded by the token being
 * hashed at rest and spent on first use.
 *
 * ⚠️ **There is deliberately no `inviteUrl(token)` helper here** (BUG-040, decided in this pass).
 * One existed, with no production caller: `InvitationDialog` composes `window.location.origin +
 * invitation.path` from the **server's** own string, which is the only composition the product makes.
 * Now that the shape carries a `#`, a client-side composer would be a *second* definition of the one
 * thing BUG-028 fixed by making exactly one place build it — and its body was
 * `encodeURIComponent(token)`, the very transformation the screen must not apply (see `index.tsx`).
 * A test that needs an address composes `` `${INVITE_PATH}#${token}` `` from the constant above.
 */
export const INVITE_PATH = '/invite';
